import React, { useState, useEffect } from 'react';
import { useSearchParams, Link, useNavigate } from 'react-router-dom';
import { format, parseISO } from 'date-fns';
import { es } from 'date-fns/locale';
import { Check, Sparkles, ShieldCheck, ArrowRight, Ticket, Info } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { useAuth } from '../context/AuthContext';
import api, { mensajeDeError, pesos } from '../lib/api';
import { toast } from 'sonner';

/**
 * Bonos de sesiones.
 *
 * Un paquete de sesiones con el mismo profesional, pagado por adelantado y con
 * descuento. No es solo comercial: la continuidad con un mismo terapeuta es
 * donde está el valor del proceso, y comprometerse de entrada ayuda a sostenerlo
 * en vez de abandonarlo tras la primera sesión.
 */
const BonosPage = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { isPatient, crearIdentidad } = useAuth();

  const psicologoId = searchParams.get('profesional');

  const [oferta, setOferta] = useState(null);
  const [misBonos, setMisBonos] = useState([]);
  const [cargando, setCargando] = useState(true);
  const [comprando, setComprando] = useState(null);

  useEffect(() => {
    const cargar = async () => {
      try {
        if (psicologoId) {
          const { data } = await api.get(`/profesionales/${psicologoId}/bonos`);
          setOferta(data);
        }
        if (isPatient) {
          const { data } = await api.get('/mi/bonos');
          setMisBonos(data);
        }
      } catch (err) {
        toast.error(mensajeDeError(err, 'No pudimos cargar los bonos'));
      } finally {
        setCargando(false);
      }
    };
    cargar();
  }, [psicologoId, isPatient]);

  const comprar = async (clave) => {
    setComprando(clave);

    try {
      // Si aún no tiene identidad anónima, se crea aquí: el bono va atado a ella.
      if (!isPatient) {
        const nueva = await crearIdentidad(null);
        if (!nueva.ok) {
          toast.error(nueva.error);
          setComprando(null);
          return;
        }
      }

      const { data } = await api.post('/bonos/comprar', {
        psychologist_id: psicologoId,
        pack: clave,
      });

      if (data.payment?.checkout_url) {
        window.location.href = data.payment.checkout_url;
        return;
      }

      toast.success('¡Tu bono quedó activo!');
      navigate('/mis-horas');
    } catch (err) {
      toast.error(mensajeDeError(err, 'No pudimos comprar el bono'));
    } finally {
      setComprando(null);
    }
  };

  if (cargando) {
    return <div className="py-24 text-center text-slate-500">Cargando…</div>;
  }

  // Sin profesional elegido, se explica y se manda a elegir uno
  if (!psicologoId) {
    return (
      <div className="min-h-[70vh] py-12 px-4">
        <div className="max-w-2xl mx-auto">
          <Encabezado />

          {misBonos.length > 0 && <MisBonos bonos={misBonos} />}

          <Card className="mt-6">
            <CardContent className="py-10 text-center">
              <Ticket className="w-10 h-10 text-slate-300 mx-auto mb-4" />
              <p className="font-medium text-slate-900 mb-2">
                Los bonos son con un profesional en particular
              </p>
              <p className="text-slate-600 mb-6 max-w-sm mx-auto">
                Elige con quién quieres trabajar y ahí verás los paquetes disponibles.
              </p>
              <div className="flex flex-col sm:flex-row gap-3 justify-center">
                <Link to="/encuentra">
                  <Button className="bg-teal-600 hover:bg-teal-700 text-white rounded-full px-8 w-full">
                    Ayúdame a elegir
                  </Button>
                </Link>
                <Link to="/profesionales">
                  <Button variant="outline" className="rounded-full px-8 w-full">
                    Ver profesionales
                  </Button>
                </Link>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-[70vh] py-12 px-4">
      <div className="max-w-3xl mx-auto">
        <Encabezado nombre={oferta?.psychologist_name} />

        {misBonos.length > 0 && <MisBonos bonos={misBonos} />}

        <div className="grid md:grid-cols-2 gap-4 mt-6">
          {oferta?.packs.map((pack, i) => (
            <Card
              key={pack.key}
              className={i === 1 ? 'border-teal-300 relative' : 'border-slate-200'}
            >
              {i === 1 && (
                <Badge className="absolute -top-2.5 left-5 bg-teal-600 text-white border-0">
                  Mejor precio por sesión
                </Badge>
              )}

              <CardContent className="p-6">
                <h3 className="font-heading text-xl font-semibold text-slate-900">{pack.name}</h3>
                <p className="text-sm text-slate-600 mt-1 mb-5">{pack.detail}</p>

                <div className="mb-5">
                  <p className="font-heading text-3xl font-semibold text-slate-900">
                    {pesos(pack.total)}
                  </p>
                  <p className="text-sm text-slate-500 mt-1">
                    <span className="line-through">{pesos(pack.full_price)}</span>{' '}
                    <span className="text-teal-700 font-medium">
                      ahorras {pesos(pack.saving)}
                    </span>
                  </p>
                </div>

                <ul className="space-y-2 mb-6 text-sm">
                  <Beneficio texto={`${pack.sessions} sesiones de 50 minutos`} />
                  <Beneficio texto={`Te queda cada una en ${pesos(pack.unit_price)}`} />
                  <Beneficio texto={`Válido por ${oferta.validity_months} meses`} />
                  <Beneficio texto="Agendas cuando quieras, sin pagar de nuevo" />
                </ul>

                <Button
                  onClick={() => comprar(pack.key)}
                  disabled={comprando !== null}
                  className={`w-full rounded-full py-6 ${
                    i === 1
                      ? 'bg-teal-600 hover:bg-teal-700 text-white'
                      : 'bg-slate-900 hover:bg-slate-800 text-white'
                  }`}
                >
                  {comprando === pack.key ? 'Preparando…' : 'Comprar este bono'}
                  {comprando !== pack.key && <ArrowRight className="w-4 h-4 ml-2" />}
                </Button>
              </CardContent>
            </Card>
          ))}
        </div>

        <div className="flex gap-3 mt-6 p-4 rounded-xl bg-slate-50 text-sm text-slate-600">
          <Info className="w-4 h-4 text-slate-400 flex-none mt-0.5" />
          <div>
            <p className="mb-1">
              Sesión suelta con {oferta?.psychologist_name}: {pesos(oferta?.single_price ?? 0)}.
            </p>
            <p>
              Si cancelas una hora con la anticipación debida, la sesión vuelve a tu bono.
            </p>
          </div>
        </div>

        <p className="flex items-center justify-center gap-2 text-xs text-slate-500 mt-6">
          <ShieldCheck className="w-4 h-4 text-teal-600" />
          El bono queda atado a tu código de continuidad. Sigues siendo anónimo.
        </p>
      </div>
    </div>
  );
};

const Encabezado = ({ nombre }) => (
  <div className="text-center mb-8">
    <div className="w-14 h-14 rounded-full bg-teal-100 flex items-center justify-center mx-auto mb-4">
      <Sparkles className="w-7 h-7 text-teal-700" />
    </div>
    <h1 className="font-heading text-3xl md:text-4xl font-semibold text-slate-900">
      Bonos de sesiones
    </h1>
    <p className="text-slate-600 mt-2 max-w-lg mx-auto">
      {nombre
        ? `Varias sesiones con ${nombre}, a mejor precio. Un proceso funciona mejor cuando tiene continuidad.`
        : 'Varias sesiones con el mismo profesional, a mejor precio.'}
    </p>
  </div>
);

const Beneficio = ({ texto }) => (
  <li className="flex items-start gap-2 text-slate-600">
    <Check className="w-4 h-4 text-teal-600 flex-none mt-0.5" />
    {texto}
  </li>
);

const MisBonos = ({ bonos }) => {
  const activos = bonos.filter((b) => b.usable);

  if (activos.length === 0) return null;

  return (
    <Card className="border-teal-200 bg-teal-50/50">
      <CardContent className="py-5">
        <p className="font-medium text-teal-900 mb-3 flex items-center gap-2">
          <Ticket className="w-4 h-4" />
          Tus bonos activos
        </p>

        <div className="space-y-2">
          {activos.map((b) => (
            <div key={b.id} className="flex flex-wrap items-center justify-between gap-3">
              <div>
                <p className="text-slate-900 font-medium">{b.psychologist_name}</p>
                <p className="text-sm text-slate-600">
                  {b.remaining} de {b.total_sessions} sesiones disponibles
                  {b.expires_at &&
                    ` · vence el ${format(parseISO(b.expires_at), "d 'de' MMMM yyyy", { locale: es })}`}
                </p>
              </div>

              <Link to={`/agendar?profesional=${b.psychologist_id}`}>
                <Button size="sm" className="bg-teal-600 hover:bg-teal-700 text-white rounded-full">
                  Usar una sesión
                </Button>
              </Link>
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
};

export default BonosPage;
